import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { SiteNavigation } from "../components/navigation/SiteNavigation";
import { 
  Search, 
  Music, 
  Calendar, 
  Users, 
  Sparkles,
  ArrowRight,
  Filter,
  MapPin,
  BadgeCheck,
  Play,
  Pause,
  Heart,
  ShoppingBag,
  Video,
  Volume2,
  SkipForward
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const Fandom = () => {
  const navigate = useNavigate();
  const [artists, setArtists] = useState([]);
  const [events, setEvents] = useState([]);
  const [content, setContent] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("artists");

  // Media player state
  const [playingId, setPlayingId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const videoRefs = useRef({});
  const progressInterval = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [artistsRes, eventsRes, contentRes, productsRes] = await Promise.all([
        axios.get(`${API}/discover/artists?limit=60`),
        axios.get(`${API}/events?status=upcoming`),
        axios.get(`${API}/content`),
        axios.get(`${API}/products`)
      ]);
      setArtists(artistsRes.data);
      setEvents(eventsRes.data);
      setContent(contentRes.data);
      setProducts(productsRes.data);
    } catch (e) {
      console.error("Failed to fetch data:", e);
    } finally {
      setLoading(false);
    }
  };

  // Audio player
  const toggleAudio = (contentId) => {
    if (playingId === contentId) {
      audioRef.current?.pause();
      clearInterval(progressInterval.current);
      setPlayingId(null);
      setProgress(0);
      setCurrentTime(0);
    } else {
      // Stop any current audio/video
      if (audioRef.current) { audioRef.current.pause(); clearInterval(progressInterval.current); }
      Object.values(videoRefs.current).forEach(v => { if (v && !v.paused) v.pause(); });
      
      const audio = new Audio(`${API}/content/${contentId}/stream`);
      audioRef.current = audio;
      audio.addEventListener('loadedmetadata', () => setDuration(audio.duration));
      audio.play().catch(() => {});
      progressInterval.current = setInterval(() => {
        if (audio.duration) {
          setProgress((audio.currentTime / audio.duration) * 100);
          setCurrentTime(audio.currentTime);
        }
      }, 200);
      audio.onended = () => { setPlayingId(null); setProgress(0); setCurrentTime(0); clearInterval(progressInterval.current); };
      setPlayingId(contentId);
    }
  };

  // Video player
  const toggleVideo = (contentId) => {
    const video = videoRefs.current[contentId];
    if (!video) return;
    if (playingId === contentId) {
      video.pause();
      clearInterval(progressInterval.current);
      setPlayingId(null);
    } else {
      if (audioRef.current) { audioRef.current.pause(); clearInterval(progressInterval.current); }
      Object.values(videoRefs.current).forEach(v => { if (v && !v.paused) v.pause(); });
      video.play().catch(() => {});
      progressInterval.current = setInterval(() => {
        if (video.duration) {
          setProgress((video.currentTime / video.duration) * 100);
          setCurrentTime(video.currentTime);
          setDuration(video.duration);
        }
      }, 200);
      video.onended = () => { setPlayingId(null); setProgress(0); clearInterval(progressInterval.current); };
      setPlayingId(contentId);
    }
  };

  const seekMedia = (e, contentId, isVideo) => {
    const bar = e.currentTarget;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    if (isVideo && videoRefs.current[contentId]) {
      videoRefs.current[contentId].currentTime = pct * videoRefs.current[contentId].duration;
    } else if (audioRef.current && playingId === contentId) {
      audioRef.current.currentTime = pct * audioRef.current.duration;
    }
    setProgress(pct * 100);
  };

  const formatTime = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec < 10 ? '0' : ''}${sec}`;
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchData();
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/artists?search=${encodeURIComponent(searchQuery)}`);
      setArtists(response.data);
    } catch (e) {
      console.error("Search failed:", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050810]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-strong public-navigation-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
          </Link>
          
          <SiteNavigation scope="fandom" />

          <Button 
            onClick={() => navigate("/")}
            className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399] font-semibold rounded-full px-6"
            data-testid="get-started-btn"
          >
            Get Started
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <h1 className="font-['Unbounded'] text-4xl sm:text-5xl font-bold text-white mb-4">
              Fandom Artists
            </h1>
            <p className="text-[#8A92A0] text-lg max-w-2xl mx-auto">
              Explore talented artists, upcoming events, and fresh content.
            </p>
          </div>

          {/* Search */}
          <div className="max-w-2xl mx-auto mb-12">
            <div className="flex gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8A92A0]" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="Search artists, genres..."
                  className="pl-12 bg-[#0A0E18] border-[#1A2030] text-white h-12"
                  data-testid="search-input"
                />
              </div>
              <Button 
                onClick={handleSearch}
                className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399] h-12 px-6"
                data-testid="search-btn"
              >
                Search
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-[#0A0E18] border border-[#1A2030] p-1 mb-8">
              <TabsTrigger 
                value="artists" 
                className="data-[state=active]:bg-[#EC008C] data-[state=active]:text-[#000000]"
                data-testid="tab-artists"
              >
                <Users className="w-4 h-4 mr-2" />
                Artists
              </TabsTrigger>
              <TabsTrigger 
                value="events"
                className="data-[state=active]:bg-[#EC008C] data-[state=active]:text-[#000000]"
                data-testid="tab-events"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Events
              </TabsTrigger>
              <TabsTrigger 
                value="content"
                className="data-[state=active]:bg-[#EC008C] data-[state=active]:text-[#000000]"
                data-testid="tab-content"
              >
                <Music className="w-4 h-4 mr-1" />
                <Video className="w-4 h-4 mr-2" />
                Content
              </TabsTrigger>
              <TabsTrigger 
                value="store"
                className="data-[state=active]:bg-[#EC008C] data-[state=active]:text-[#000000]"
                data-testid="tab-store"
              >
                <ShoppingBag className="w-4 h-4 mr-2" />
                Artist Store
              </TabsTrigger>
            </TabsList>

            {loading ? (
              <div className="flex justify-center py-20">
                <div className="spinner"></div>
              </div>
            ) : (
              <>
                {/* Artists Tab */}
                <TabsContent value="artists">
                  {artists.length === 0 ? (
                    <div className="card p-12 text-center">
                      <Users className="w-12 h-12 text-[#8A92A0] mx-auto mb-4" />
                      <p className="text-[#8A92A0]">No artists found. Be the first to join!</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                      {artists.map((artist) => (
                        <Link
                          key={artist.artist_id}
                          to={`/artist/${artist.artist_id}`}
                          className="card p-6 group"
                          data-testid={`artist-card-${artist.artist_id}`}
                        >
                          <div className="w-20 h-20 rounded-full bg-[#1A2030] mx-auto mb-4 overflow-hidden">
                            {artist.profile_image ? (
                              <img src={artist.profile_image} alt={artist.stage_name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Users className="w-8 h-8 text-[#8A92A0]" />
                              </div>
                            )}
                          </div>
                          <div className="flex items-center justify-center gap-1.5 mb-1">
                            <h3 className="font-['Unbounded'] text-lg font-semibold text-white group-hover:text-[#EC008C] transition-colors truncate">
                              {artist.stage_name}
                            </h3>
                            {artist.is_verified && (
                              <BadgeCheck className="w-5 h-5 text-[#EC008C] flex-shrink-0" data-testid={`verified-${artist.artist_id}`} />
                            )}
                          </div>
                          {artist.genre && (
                            <p className="text-sm text-[#EC008C] text-center mb-2">{artist.genre}</p>
                          )}
                          {artist.location && (
                            <p className="text-xs text-[#8A92A0] text-center flex items-center justify-center gap-1">
                              <MapPin className="w-3 h-3" /> {artist.location}
                            </p>
                          )}
                        </Link>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Events Tab */}
                <TabsContent value="events">
                  {events.length === 0 ? (
                    <div className="card p-12 text-center">
                      <Calendar className="w-12 h-12 text-[#8A92A0] mx-auto mb-4" />
                      <p className="text-[#8A92A0]">No upcoming events.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {events.map((event) => (
                        <Link
                          key={event.event_id}
                          to={`/events/${event.event_id}`}
                          className="event-card card p-6 group"
                          data-testid={`event-card-${event.event_id}`}
                        >
                          <div className="aspect-video rounded-lg bg-[#1A2030] mb-4 overflow-hidden">
                            {event.cover_image ? (
                              <img src={event.cover_image} alt={event.title} className="w-full h-full object-cover" />
                            ) : (
                              <img src="https://customer-assets.emergentagent.com/job_creator-vault-54/artifacts/8xs65yky_Sold-out%20concert%20frenzy%20under%20vibrant%20lights.png" alt={event.title} className="w-full h-full object-cover" />
                            )}
                          </div>
                          <h3 className="font-['Unbounded'] text-lg font-semibold text-white mb-2 group-hover:text-[#EC008C] transition-colors">
                            {event.title}
                          </h3>
                          <p className="text-[#8A92A0] text-sm mb-3 flex items-center gap-1">
                            <MapPin className="w-4 h-4" /> {event.venue} • {event.location}
                          </p>
                          <div className="flex items-center justify-between">
                            <span className="text-[#EC008C] font-semibold">${event.ticket_price}</span>
                            <span className="text-sm text-[#8A92A0]">{event.event_date}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Content Tab */}
                <TabsContent value="content">
                  {content.length === 0 ? (
                    <div className="card p-12 text-center">
                      <Music className="w-12 h-12 text-[#8A92A0] mx-auto mb-4" />
                      <p className="text-[#8A92A0]">No content available yet.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      {content.map((item) => {
                        const isPlaying = playingId === item.content_id;
                        const isVideo = item.content_type === "video" || item.content_type === "livestream";
                        
                        return (
                          <div key={item.content_id} className="card overflow-hidden group" data-testid={`content-card-${item.content_id}`}>
                            {/* Player Area */}
                            <div className="aspect-video bg-[#0A0E18] relative">
                              {isVideo ? (
                                <>
                                  <video
                                    ref={(el) => { videoRefs.current[item.content_id] = el; }}
                                    src={`${API}/content/${item.content_id}/stream`}
                                    className="w-full h-full object-contain bg-black"
                                    preload="metadata"
                                    playsInline
                                    data-testid={`video-player-${item.content_id}`}
                                  />
                                  <div 
                                    className={`absolute inset-0 flex items-center justify-center cursor-pointer transition-all ${isPlaying ? 'bg-transparent' : 'bg-black/40'}`}
                                    onClick={() => toggleVideo(item.content_id)}
                                  >
                                    {!isPlaying && (
                                      <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur flex items-center justify-center group-hover:scale-110 transition-transform">
                                        <Play className="w-6 h-6 text-white ml-1" />
                                      </div>
                                    )}
                                  </div>
                                  {isPlaying && (
                                    <button className="absolute top-3 left-3 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center" onClick={() => toggleVideo(item.content_id)}>
                                      <Pause className="w-4 h-4 text-white" />
                                    </button>
                                  )}
                                </>
                              ) : (
                                <div 
                                  className="w-full h-full flex items-center justify-center cursor-pointer bg-gradient-to-br from-[#121214] to-[#1a1a2e]"
                                  onClick={() => toggleAudio(item.content_id)}
                                  data-testid={`audio-player-${item.content_id}`}
                                >
                                  {item.thumbnail ? (
                                    <img src={item.thumbnail} alt={item.title} className="w-full h-full object-cover absolute inset-0" />
                                  ) : (
                                    <Music className="w-12 h-12 text-[#EC008C]/40" />
                                  )}
                                  <div className={`absolute inset-0 flex items-center justify-center transition-all ${isPlaying ? 'bg-black/30' : 'bg-black/0 hover:bg-black/40'}`}>
                                    <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                                      isPlaying ? 'bg-[#EC008C] scale-100' : 'bg-white/20 backdrop-blur scale-0 group-hover:scale-100'
                                    }`}>
                                      {isPlaying ? <Pause className="w-6 h-6 text-[#000000]" /> : <Play className="w-6 h-6 text-white ml-1" />}
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              {/* Progress bar */}
                              {isPlaying && (
                                <div 
                                  className="absolute bottom-0 left-0 right-0 h-1.5 bg-[#1A2030] cursor-pointer"
                                  onClick={(e) => seekMedia(e, item.content_id, isVideo)}
                                  data-testid={`seek-bar-${item.content_id}`}
                                >
                                  <div className="h-full bg-[#EC008C] rounded-r transition-all duration-200" style={{ width: `${progress}%` }} />
                                </div>
                              )}
                              
                              {/* Type badge */}
                              <span className={`absolute top-3 right-3 px-2 py-0.5 rounded text-xs font-medium ${
                                isVideo ? "bg-[#FF6BC5]/20 text-[#FF6BC5]" : "bg-[#EC008C]/20 text-[#EC008C]"
                              }`}>
                                {item.content_type}
                              </span>
                            </div>
                            
                            {/* Info */}
                            <div className="p-4">
                              <h3 className="font-semibold text-white mb-1 truncate">{item.title}</h3>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-[#8A92A0]">{(item.plays || 0).toLocaleString()} plays</span>
                                {isPlaying && (
                                  <span className="text-[#EC008C] text-xs font-mono">{formatTime(currentTime)} / {formatTime(duration)}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>

                {/* Artist Store Tab */}
                <TabsContent value="store">
                  {products.length === 0 ? (
                    <div className="card p-12 text-center">
                      <ShoppingBag className="w-12 h-12 text-[#8A92A0] mx-auto mb-4" />
                      <p className="text-[#8A92A0]">No products listed yet.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                      {products.map((product) => (
                        <Link
                          key={product.product_id}
                          to={`/products/${product.product_id}`}
                          className="card p-4 group relative"
                          data-testid={`store-product-${product.product_id}`}
                        >
                          <span
                            className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#EC008C]/95 text-[10px] font-bold uppercase tracking-widest text-black shadow-lg"
                            data-testid={`store-product-live-${product.product_id}`}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live
                          </span>
                          <div className="aspect-square rounded-lg bg-[#1A2030] mb-3 flex items-center justify-center overflow-hidden">
                            {product.images && product.images.length > 0 ? (
                              <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                            ) : (
                              <img src="https://customer-assets.emergentagent.com/job_creator-vault-54/artifacts/am5ifx80_Korean%20idols%20in%20casual%20fashion.%20%282%29.png" alt={product.name} className="w-full h-full object-cover" />
                            )}
                          </div>
                          <h3 className="font-semibold text-white mb-1 truncate group-hover:text-[#EC008C] transition-colors">{product.name}</h3>
                          <p className="text-sm text-[#8A92A0] mb-2 capitalize">{product.category}</p>
                          <div className="flex items-center justify-between">
                            <div className="flex flex-col leading-tight">
                              <span className="text-[#EC008C] font-semibold" data-testid={`store-product-price-usd-${product.product_id}`}>
                                ${Number(product.price || 0).toFixed(2)}
                              </span>
                              <span className="text-[10px] text-[#8A92A0] uppercase tracking-widest" data-testid={`store-product-price-tokens-${product.product_id}`}>
                                {Number(product.price || 0).toFixed(0)} Tokens
                              </span>
                            </div>
                            <span className="text-xs text-[#8A92A0]">{product.stock} left</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </>
            )}
          </Tabs>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-[#1A2030]">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs text-[#6A7280] text-center mb-6">
            StardomFans.com is not responsible for any content uploaded by artists. All content is the sole responsibility of the respective creator. <Link to="/terms" className="text-[#EC008C] hover:underline">Terms of Service</Link>
          </p>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="SF" className="w-10 h-10 object-contain" style={{mixBlendMode:"lighten"}} />
            </div>
            <div className="flex gap-6 flex-wrap justify-center">
              <Link to="/terms" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Terms</Link>
              <Link to="/privacy" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Privacy</Link>
              <Link to="/copyright" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Copyright</Link>
              <Link to="/security" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Security</Link>
            </div>
            <p className="text-[#8A92A0] text-sm">&copy; {new Date().getFullYear()} StardomFans Ltd.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Fandom;
